import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Video, ShoppingCart, Car, CreditCard, FileText, Wrench, Shield, TrendingUp, Send, Search, Users, Package, Settings, Gauge, Handshake, HandCoins, X } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { VehicleSelector } from '@/components/VehicleSelector';
import { FipeVehicleSelector } from '@/components/FipeVehicleSelector';
import { FipeTable } from '@/components/FipeTable';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { trackLeadSubmission, trackServiceRequest } from '@/lib/analytics';
import { CardTitle } from './ui/card';
import { useIsLayout4 } from '@/hooks/useIsLayout4';
import { layout4ThemeStyle } from '@/layouts/layout-4/theme';
import '@/layouts/layout-4/styles.css';

interface ServiceModalProps {
  service: any;
  serviceTitle: any;
  config: any;
  children?: React.ReactNode;
  preSelectedVehicle?: any;
  isOpen?: boolean; // Controlled open state
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void; // New callback for when modal closes
  renderInline?: boolean; // New prop to render form inline without modal
  serviceImage?: string; // Image for the service
}

export const ServiceModal = ({ service, serviceTitle, config, children, preSelectedVehicle, isOpen: controlledIsOpen, onOpenChange, onClose, renderInline = false, serviceImage }: ServiceModalProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [selectedVehicles, setSelectedVehicles] = useState<Record<string, any>>({});
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { vehicles } = useAppData();
  const { toast } = useToast();
  const { colors } = useTheme();
  const isLayout4 = useIsLayout4();
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;

  // Check if this is FIPE Table service
  const isFipeTable = service.service === '65dcb7fe8eca108d87c91dbe';

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Pre-fill vehicle when modal opens with preSelectedVehicle
  const handleOpenChange = (open: boolean) => {
    // Update state
    if (typeof setIsOpen === 'function') {
      setIsOpen(open);
    }

    // Call onClose callback when closing
    if (onClose && !open) {
      onClose();
    }

    if (open && preSelectedVehicle) {
      // Pre-fill vehicle fields
      const vehicleText = formatVehicleForMessage(preSelectedVehicle);
      const price = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(preSelectedVehicle.price);

      setSelectedVehicles({ 'veiculo': preSelectedVehicle, 'vehicle': preSelectedVehicle });
      setFormData({
        'veiculo': vehicleText,
        'vehicle': vehicleText,
        'preco': price,
        'price': price
      });
    } else if (!open) {
      // Reset all form data when closing
      setFormData({});
      setSelectedVehicles({});
    }
  };

  // Format vehicle for display in select
  const formatVehicle = (vehicle: any) => {
    const price = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(vehicle.price);

    return `${vehicle.brand} ${vehicle.model} ${vehicle.modelYear} - ${price}`;
  };

  // Format vehicle for message (more detailed)
  const formatVehicleForMessage = (vehicle: any) => {
    return `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear}`;
  };

  // Parse the lead message to find required fields
  const parseLeadMessage = (message: string) => {
    const fields: string[] = [];
    const regex = /\{(\w+)\}/g;
    let match;

    while ((match = regex.exec(message)) !== null) {
      if (!fields.includes(match[1])) {
        fields.push(match[1]);
      }
    }

    return fields;
  };

  // Get required fields and define custom order for specific services
  const requiredFields = service.lead?.ios ? parseLeadMessage(service.lead?.ios) : [];

  // Custom field order for specific services
  const getFieldOrder = (serviceId: string) => {
    const customOrders: Record<string, string[]> = {
      '65da42048eca108d87c91da6': ['nome', 'name', 'veiculo', 'vehicle', 'datahora', 'datetime'], // Videochamada
      '65dcb7548eca108d87c91dbb': ['nome', 'name', 'veiculo', 'vehicle', 'km', 'mileage'], // Avaliação
      '65dcb7c58eca108d87c91dbd': ['nome', 'name', 'veiculo', 'vehicle', 'km', 'mileage', 'valor', 'value'], // Consignação
      '65dcb7898eca108d87c91dbc': ['nome', 'name', 'nascimento', 'birthdate', 'cpf', 'taxid', 'veiculo', 'vehicle', 'preco', 'price'], // Financiamento
      '65dcb5e78eca108d87c91db8': ['nome', 'name', 'cnh', 'driverlicense', 'veiculo', 'vehicle', 'datahora', 'datetime'], // Test-Drive
      '65dcef468eca108d87c91dc8': ['nome', 'name', 'nascimento', 'birthdate', 'cpf', 'taxid', 'valor', 'value'], // Consórcio
      '65dcb6528eca108d87c91db9': ['nome', 'name', 'veiculo', 'vehicle'], // Despachante
      '65dcb7238eca108d87c91dba': ['nome', 'name', 'cpf', 'taxid', 'veiculo', 'vehicle'], // Seguros
      '65dcef098eca108d87c91dc7': ['nome', 'name', 'cpf', 'taxid', 'veiculo', 'vehicle', 'km', 'mileage'], // Revisões
      '65dceeac8eca108d87c91dc6': ['nome', 'name', 'cpf', 'taxid', 'veiculo', 'vehicle', 'km', 'mileage'], // Serviços
      '65dcb8548eca108d87c91dbf': ['nome', 'name'], // Vendas Diretas
      '6628fd535bda2ae51f6fc5ff': ['nome', 'name', 'cpf', 'taxid', 'veiculo', 'vehicle'], // Peças e Acessórios
    };

    return customOrders[serviceId] || [];
  };

  // Sort fields based on custom order
  const sortedFields = (() => {
    const customOrder = getFieldOrder(service.service);
    if (customOrder.length === 0) return requiredFields;

    return [...requiredFields].sort((a, b) => {
      const indexA = customOrder.indexOf(a);
      const indexB = customOrder.indexOf(b);

      // If both are in custom order, sort by that order
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      // If only A is in custom order, it comes first
      if (indexA !== -1) return -1;
      // If only B is in custom order, it comes first
      if (indexB !== -1) return 1;
      // Otherwise maintain original order
      return 0;
    });
  })();

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      'nome': 'Nome',
      'name': 'Nome',
      'veiculo': 'Veículo',
      'vehicle': 'Veículo',
      'preco': 'Preço',
      'price': 'Preço',
      'km': 'Quilometragem',
      'mileage': 'Quilometragem',
      'cpf': 'CPF',
      'taxid': 'CPF',
      'cnh': 'CNH',
      'driverlicense': 'CNH',
      'driveslicense': 'CNH',
      'nascimento': 'Data de Nascimento',
      'birthdate': 'Data de Nascimento',
      'valor': 'Valor',
      'value': 'Valor',
      'datahora': 'Data e Hora',
      'datetime': 'Data e Hora',
      'telefone': 'Telefone',
      'phone': 'Telefone',
      'email': 'E-mail',
      'mensagem': 'Mensagem',
      'message': 'Mensagem'
    };

    return labels[field] || field.charAt(0).toUpperCase() + field.slice(1);
  };

  const getFieldType = (field: string) => {
    if (field.includes('nascimento') || field.includes('birthdate')) {
      return 'date';
    }
    if (field.includes('datahora') || field.includes('datetime')) {
      return 'datetime-local';
    }
    // Valor/Value fields use text type for currency mask
    if (field.includes('valor') || field.includes('value')) {
      return 'text';
    }
    if (field.includes('preco') || field.includes('price') || field.includes('km') || field.includes('mileage')) {
      return 'number';
    }
    if (field.includes('email')) {
      return 'email';
    }
    if (field.includes('telefone') || field.includes('phone')) {
      return 'tel';
    }
    return 'text';
  };

  const formatCPF = (value: string) => {
    // Remove non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Apply CPF mask: XXX.XXX.XXX-XX
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    } else {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }
  };

  const formatCurrency = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    if (!numbers) return '';

    // Convert to number and format
    const numberValue = parseInt(numbers) / 100;

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numberValue);
  };

  const handleInputChange = (field: string, value: string) => {
    // Apply CPF formatting if field is CPF
    if (field === 'cpf' || field === 'taxid') {
      const formattedValue = formatCPF(value);
      setFormData(prev => ({ ...prev, [field]: formattedValue }));
    }
    // Apply currency formatting if field is valor/value
    else if (field === 'valor' || field === 'value') {
      const formattedValue = formatCurrency(value);
      setFormData(prev => ({ ...prev, [field]: formattedValue }));
    }
    else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleOptionToggle = (optionTitle: string) => {
    setSelectedOptions(prev => {
      const isSelected = prev.includes(optionTitle);
      const newSelection = isSelected
        ? prev.filter(opt => opt !== optionTitle)
        : [...prev, optionTitle];

      // Update formData with the new selection
      const optionsText = newSelection.join(', ');

      // Map options to correct field based on service
      if (service.service === '6628fd535bda2ae51f6fc5ff' || service.service === '65dcb8548eca108d87c91dbf') {
        // Peças e Acessórios / Vendas Diretas -> segmento
        setFormData(prevData => ({
          ...prevData,
          'segmento': optionsText,
          'segment': optionsText
        }));
      } else if (service.service === '65dceeac8eca108d87c91dc6') {
        // Serviços -> servicos
        setFormData(prevData => ({
          ...prevData,
          'servicos': optionsText,
          'services': optionsText
        }));
      } else if (service.service === '65dcef098eca108d87c91dc7') {
        // Revisões -> revisoes/revisao
        setFormData(prevData => ({
          ...prevData,
          'revisoes': optionsText,
          'revisions': optionsText,
          'revisao': optionsText,
          'revision': optionsText
        }));
      } else {
        // Others -> opcoes
        setFormData(prevData => ({
          ...prevData,
          'opcoes': optionsText,
          'options': optionsText
        }));
      }

      return newSelection;
    });
  };

  const handleSingleOptionChange = (optionTitle: string) => {
    setSelectedOptions([optionTitle]);

    // Map options to correct field based on service
    if (service.service === '6628fd535bda2ae51f6fc5ff' || service.service === '65dcb8548eca108d87c91dbf') {
      // Peças e Acessórios / Vendas Diretas -> segmento
      setFormData(prev => ({
        ...prev,
        'segmento': optionTitle,
        'segment': optionTitle
      }));
    } else if (service.service === '65dceeac8eca108d87c91dc6') {
      // Serviços -> servicos
      setFormData(prev => ({
        ...prev,
        'servicos': optionTitle,
        'services': optionTitle
      }));
    } else if (service.service === '65dcef098eca108d87c91dc7') {
      // Revisões -> revisoes/revisao
      setFormData(prev => ({
        ...prev,
        'revisoes': optionTitle,
        'revisions': optionTitle,
        'revisao': optionTitle,
        'revision': optionTitle
      }));
    } else {
      // Others -> opcoes
      setFormData(prev => ({
        ...prev,
        'opcoes': optionTitle,
        'options': optionTitle
      }));
    }
  };

  const handleVehicleSelect = (field: string, vehicle: any) => {
    setSelectedVehicles(prev => ({ ...prev, [field]: vehicle }));
    const vehicleText = formatVehicleForMessage(vehicle);

    // Format price
    const price = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(vehicle.price);

    // Update form data with vehicle and automatically set price
    setFormData(prev => ({
      ...prev,
      [field]: vehicleText,
      'preco': price,
      'price': price
    }));
  };

  const getServiceIcon = () => {
    const serviceIcons: Record<string, any> = {
      '65da42048eca108d87c91da6': Video,           // Videochamada
      '65dcb7548eca108d87c91dbb': Search,          // Avaliação
      '65dcb7c58eca108d87c91dbd': HandCoins,       // Consignação
      '65dcef468eca108d87c91dc8': Users,           // Consórcio
      '65dcb6528eca108d87c91db9': FileText,        // Despachante
      '65dcb7898eca108d87c91dbc': CreditCard,      // Financiamento
      '6628fd535bda2ae51f6fc5ff': Package,         // Peças e Acessórios
      '65dcef098eca108d87c91dc7': Wrench,          // Revisões
      '65dcb7238eca108d87c91dba': Shield,          // Seguros
      '65dceeac8eca108d87c91dc6': Settings,        // Serviços
      '65dcb7fe8eca108d87c91dbe': TrendingUp,      // Tabela FIPE
      '65dcb5e78eca108d87c91db8': Gauge,           // Test-Drive
      '65dcb8548eca108d87c91dbf': Handshake        // Vendas Diretas
    };

    return serviceIcons[service.service] || ShoppingCart;
  };

  const getServiceTitle = () => {
    const serviceTitles: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar Videochamada',
      '65dcb7548eca108d87c91dbb': 'Avaliação',
      '65dcb7c58eca108d87c91dbd': 'Consignação',
      '65dcef468eca108d87c91dc8': 'Consórcio',
      '65dcb6528eca108d87c91db9': 'Despachante',
      '65dcb7898eca108d87c91dbc': 'Financiamento',
      '6628fd535bda2ae51f6fc5ff': 'Peças e Acessórios',
      '65dcef098eca108d87c91dc7': 'Revisões',
      '65dcb7238eca108d87c91dba': 'Seguros',
      '65dceeac8eca108d87c91dc6': 'Serviços',
      '65dcb7fe8eca108d87c91dbe': 'Tabela FIPE',
      '65dcb5e78eca108d87c91db8': 'Test-Drive',
      '65dcb8548eca108d87c91dbf': 'Vendas Diretas'
    };

    let title = serviceTitles[service.service] || 'Solicitar Serviço';

    // For Consignação, add options to the title
    if (service.service === '65dcb7c58eca108d87c91dbd' && service.options && service.options.length > 0) {
      const optionTitles = service.options.map((opt: any) => opt.title).join(' / ');
      title = `${title} (${optionTitles})`;
    }

    return title;
  };

  const buildMessage = () => {
    let message = service.lead?.ios || '';
    const whatsappTemplate = message.substring(0, message.indexOf('&text=') + 6);
    message = message.substring(message.indexOf('&text=') + 6);

    // Replace placeholders with form data
    Object.entries(formData).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      message = message.replace(regex, value);
    });

    // Add current date/time if needed
    const now = new Date();
    message = message.replace(/\{datahora\}/g, now.toLocaleString('pt-BR'));
    message = message.replace(/\{datetime\}/g, now.toLocaleString('pt-BR'));


    return whatsappTemplate + encodeURIComponent(message);
  };

  const handleSubmit = async () => {
    // Validate required fields (excluding fields that are filtered out or come from options)
    const fieldsToValidate = sortedFields.filter(field =>
      field !== 'preco' &&
      field !== 'price' &&
      field !== 'segmento' &&
      field !== 'segment' &&
      field !== 'opcoes' &&
      field !== 'options' &&
      field !== 'opcao' &&
      field !== 'option' &&
      field !== 'servicos' &&
      field !== 'services' &&
      field !== 'servico' &&
      field !== 'service' &&
      field !== 'revisoes' &&
      field !== 'revisions' &&
      field !== 'revisao' &&
      field !== 'revision' &&
      field !== 'mensagem' &&
      field !== 'message'
    );

    // Validate fields one by one
    for (const field of fieldsToValidate) {
      if (!formData[field] || formData[field].trim() === '') {
        toast({
          title: "⚠️ Campo obrigatório",
          description: `Por favor, preencha o campo ${getFieldLabel(field)}.`,
          variant: "destructive"
        });
        return;
      }
    }

    // Validate options selection for services that have options (except Consignação)
    if (service.options && service.options.length > 0 && service.service !== '65dcb7c58eca108d87c91dbd') {
      if (selectedOptions.length === 0) {
        const optionLabel = service.service === '6628fd535bda2ae51f6fc5ff' || service.service === '65dcb8548eca108d87c91dbf'
          ? 'Segmento'
          : service.service === '65dceeac8eca108d87c91dc6'
            ? 'Serviços'
            : service.service === '65dcef098eca108d87c91dc7'
              ? 'Revisões'
              : 'Opções';

        toast({
          title: "⚠️ Campo obrigatório",
          description: `Por favor, selecione ao menos uma opção em ${optionLabel}.`,
          variant: "destructive"
        });
        return;
      }
    }

    // Validate message field if it's required
    if (requiredFields.includes('mensagem') || requiredFields.includes('message')) {
      const messageField = requiredFields.includes('mensagem') ? 'mensagem' : 'message';
      if (!formData[messageField] || formData[messageField].trim() === '') {
        toast({
          title: "⚠️ Campo obrigatório",
          description: "Por favor, preencha o campo Mensagem.",
          variant: "destructive"
        });
        return;
      }
    }

    // Check if it's Financing service with API type
    const isFinancingApiType = service.service === '65dcb7898eca108d87c91dbc' && (service.lead?.type === 'api' || service.lead?.type === 'email');

    if (isFinancingApiType) {
      setIsSubmitting(true);

      // Send via API for Financing service
      try {
        const { LEAD } = await import('@/lib/api');

        // Get the selected vehicle from preSelectedVehicle or selectedVehicles
        const vehicle = preSelectedVehicle || Object.values(selectedVehicles)[0];

        if (!vehicle) {
          toast({
            title: "⚠️ Erro",
            description: "Veículo não encontrado.",
            variant: "destructive"
          });
          return;
        }

        // Format price helper
        const formatPrice = (price: number) => {
          return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(price);
        };

        // Prepare lead data
        const leadData = {
          name: formData.nome || formData.name || '',
          email: formData.email || '',
          phone: (formData.telefone || formData.phone || '').replace(/\D/g, ''),
          message: formData.mensagem || formData.message || '',
          vehicle: `${vehicle.brand} ${vehicle.model} ${vehicle.version ? `${vehicle.version} ` : ''}${vehicle.manufactureYear}/${vehicle.modelYear}`,
          id: vehicle.id,
          plaque: vehicle.plaque || '',
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version || '',
          year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
          price: formatPrice(vehicle.price),
          mileage: `${vehicle.mileage}km`
        };

        // Get store from config (first store or selected store)
        const store = config.stores?.[0];

        const response = await LEAD(service.service, store, leadData);

        if (response?.status == 200) {
          // Track lead submission
          trackLeadSubmission({
            name: leadData.name,
            email: leadData.email,
            phone: leadData.phone,
            message: leadData.message,
            serviceType: service.service,
            vehicle: vehicle ? {
              id: vehicle.id,
              brand: vehicle.brand,
              model: vehicle.model,
              version: vehicle.version || '',
              year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
              price: vehicle.price,
              mileage: vehicle.mileage
            } : undefined
          });

          toast({
            title: 'Mensagem enviada!',
            description: 'Entraremos em contato em breve.',
          });

          // Reset form and close modal
          setFormData({});
          setSelectedVehicles({});
          setSelectedOptions([]);
          setIsOpen(false);
        } else {
          toast({
            title: 'Erro ao enviar',
            description: 'Tente novamente.',
            variant: 'destructive'
          });
        }

        setIsSubmitting(false);
      } catch (error) {
        console.error('Error submitting lead:', error);
        toast({
          title: 'Erro ao enviar',
          description: 'Ocorreu um erro ao processar sua solicitação.',
          variant: 'destructive'
        });
        setIsSubmitting(true);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Send via WhatsApp (default behavior)
      const whatsappUrl = buildMessage();

      // Show loading toast
      toast({
        title: "✅ Redirecionando",
        description: "Abrindo WhatsApp para enviar sua mensagem...",
      });

      // Track service request
      const vehicle = preSelectedVehicle || Object.values(selectedVehicles)[0];
      trackServiceRequest(service.service, vehicle ? {
        id: vehicle.id,
        brand: vehicle.brand,
        model: vehicle.model,
        version: vehicle.version || '',
        year: `${vehicle.manufactureYear}/${vehicle.modelYear}`,
        price: vehicle.price,
        mileage: vehicle.mileage
      } : undefined);

      // Open WhatsApp with fallback to web version
      openWhatsApp(whatsappUrl).then(() => {
        // Close modal and reset form after WhatsApp opens
        setIsOpen(false);
        setFormData({});
        setSelectedVehicles({});
        setSelectedOptions([]);
      }).catch((error) => {
        console.error('Error opening WhatsApp:', error);
        toast({
          title: "❌ Erro",
          description: "Não foi possível abrir o WhatsApp. Tente novamente.",
          variant: "destructive"
        });
      });
    }
  };

  // Pre-fill vehicle for inline rendering
  // IMPORTANT: All hooks must be called before any early returns
  useEffect(() => {
    if (renderInline && preSelectedVehicle) {
      const vehicleText = formatVehicleForMessage(preSelectedVehicle);
      const price = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(preSelectedVehicle.price);

      setSelectedVehicles({ 'veiculo': preSelectedVehicle, 'vehicle': preSelectedVehicle });
      setFormData({
        'veiculo': vehicleText,
        'vehicle': vehicleText,
        'preco': price,
        'price': price
      });
    }
  }, [renderInline, preSelectedVehicle]);

  // If it's FIPE Table service, render FipeTable component
  // IMPORTANT: This must be AFTER all hooks are called
  if (isFipeTable) {
    return (
      <>
        <div onClick={() => setIsOpen(true)}>
          {children}
        </div>
        <FipeTable
          service={service}
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open && onClose) {
              onClose();
            }
          }}
          serviceImage={serviceImage}
          serviceTitle={getServiceTitle()}
        />
      </>
    );
  }

  // Form content (reusable)
  // Check if it's Financing service with API type
  const isFinancingApiType = service.service === '65dcb7898eca108d87c91dbc' && (service.lead?.type === 'api' || service.lead?.type === 'email');

  const isInlineFinancingOnDetail =
    renderInline &&
    !!preSelectedVehicle &&
    service.service === '65dcb7898eca108d87c91dbc';

  const renderModalTextField = (field: string, fieldId?: string) => {
    const id = fieldId || field;
    if (isLayout4) {
      return (
        <div className="wl4-service-field">
          <Label htmlFor={id} className="wl4-service-label">
            {getFieldLabel(field)}{' '}
            <span className="wl4-service-required">*</span>
          </Label>
          <Input
            id={id}
            type={getFieldType(field)}
            value={formData[field] || ''}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="wl4-service-input"
          />
        </div>
      );
    }

    return (
      <div className="relative">
        <Input
          id={id}
          type={getFieldType(field)}
          placeholder=" "
          value={formData[field] || ''}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className="h-12 peer pt-4 placeholder-transparent"
        />
        <Label
          htmlFor={id}
          className="absolute left-3 top-3 text-sm text-muted-foreground transition-all pointer-events-none peer-focus:top-1 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs"
        >
          {getFieldLabel(field)} <span className="text-red-500">*</span>
        </Label>
      </div>
    );
  };

  const renderModalContactField = (
    id: string,
    label: string,
    type: string,
    value: string,
    onChange: (value: string) => void
  ) => {
    if (isLayout4) {
      return (
        <div className="wl4-service-field">
          <Label htmlFor={id} className="wl4-service-label">
            {label} <span className="wl4-service-required">*</span>
          </Label>
          <Input
            id={id}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="wl4-service-input"
          />
        </div>
      );
    }

    return (
      <div className="relative">
        <Input
          id={id}
          type={type}
          placeholder=" "
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-12 peer pt-4 placeholder-transparent"
        />
        <Label
          htmlFor={id}
          className="absolute left-3 top-3 text-sm text-muted-foreground transition-all pointer-events-none peer-focus:top-1 peer-focus:text-xs peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-1 peer-[:not(:placeholder-shown)]:text-xs"
        >
          {label} <span className="text-red-500">*</span>
        </Label>
      </div>
    );
  };

  const renderModalMessageField = (
    id: string,
    value: string,
    onChange: (value: string) => void
  ) => {
    if (isLayout4) {
      return (
        <div className="wl4-service-field">
          <Label htmlFor={id} className="wl4-service-label">
            Mensagem <span className="wl4-service-required">*</span>
          </Label>
          <Textarea
            id={id}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            className="wl4-service-textarea"
          />
        </div>
      );
    }

    return (
      <div className="relative">
        <Textarea
          id={id}
          placeholder=" "
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="resize-none peer pt-6 placeholder-transparent min-h-[100px]"
        />
        <div className="absolute left-0 top-0 right-0 h-6 bg-background border-b border-input pointer-events-none peer-focus:border-primary/50 transition-colors" />
        <Label
          htmlFor={id}
          className="absolute left-3 top-2 text-sm text-muted-foreground transition-all pointer-events-none z-10 peer-focus:text-primary"
        >
          Mensagem <span className="text-red-500">*</span>
        </Label>
      </div>
    );
  };

  const renderModalHeader = () => {
    const IconComponent = getServiceIcon();
    const title = getServiceTitle();

    if (serviceImage && isLayout4) {
      return (
        <div className="wl4-service-modal-image-head">
          <img src={serviceImage} alt={title} />
          <div className="wl4-service-modal-image-overlay">
            <div className="wl4-service-modal-head-icon">
              <IconComponent className="h-6 w-6" />
            </div>
            <div>
              <h2>{title}</h2>
              <p>Preencha o formulário abaixo</p>
            </div>
          </div>
          <button
            type="button"
            className="wl4-service-modal-close"
            onClick={() => setIsOpen(false)}
            aria-label="Fechar modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }

    if (serviceImage) {
      return (
        <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
          <img src={serviceImage} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 h-10 w-10 bg-white/20 hover:bg-white/25 backdrop-blur-sm border border-white/20 text-white z-20"
            onClick={() => setIsOpen(false)}
            aria-label="Fechar modal"
          >
            <X className="h-5 w-5 text-white" />
          </Button>
          <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20">
                <IconComponent className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white drop-shadow-lg">{title}</h2>
                <p className="text-sm text-white/80">Preencha o formulário abaixo!</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isLayout4) {
      return (
        <div className="wl4-service-modal-head">
          <div className="wl4-service-modal-head-icon">
            <IconComponent className="h-5 w-5" />
          </div>
          <div className="wl4-service-modal-head-text">
            <h2>{title}</h2>
            <p>Preencha os dados para solicitar este serviço</p>
          </div>
          <button
            type="button"
            className="wl4-service-modal-close"
            onClick={() => setIsOpen(false)}
            aria-label="Fechar modal"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      );
    }

    return (
      <DialogHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4 rounded-t-lg">
        <div className="flex items-center justify-between">
          <DialogTitle className="flex items-center gap-2">
            <IconComponent className="h-5 w-5" />
            <span style={{ color: titleColor }}>{title}</span>
          </DialogTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={() => setIsOpen(false)}
            aria-label="Fechar modal"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </DialogHeader>
    );
  };

  const renderServiceWarning = () => {
    if (!service.warning) return null;
    if (isInlineFinancingOnDetail) return null;

    if (isLayout4) {
      return (
        <div className="wl4-service-warning">
          {service.warning}
        </div>
      );
    }

    return (
      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-6">
        <p className="text-sm text-blue-800 dark:text-blue-200">{service.warning}</p>
      </div>
    );
  };

  // Handle vehicle selection for Financing API form
  const handleFinancingVehicleSelect = (vehicle: any) => {
    setSelectedVehicles({ vehicle });

    // Format price
    const formatPrice = (price: number) => {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(price);
    };

    // Generate default message
    const defaultMessage = `Olá,\n\nTenho interesse em simular o financiamento do veículo:\n\n${vehicle.brand} ${vehicle.model} ${vehicle.version || ''} ${vehicle.modelYear}\nPreço: ${formatPrice(vehicle.price)}\n\nGostaria de receber mais informações sobre as condições de financiamento.`;

    setFormData(prev => ({
      ...prev,
      mensagem: defaultMessage,
      message: defaultMessage
    }));
  };

  // Render API form for Financing when type is 'api'
  const formContent = isFinancingApiType ? (
    <div className="space-y-4">
      {renderModalContactField(
        'name',
        'Nome',
        'text',
        formData.nome || formData.name || '',
        (value) => setFormData(prev => ({ ...prev, nome: value, name: value }))
      )}

      {renderModalContactField(
        'email',
        'E-mail',
        'email',
        formData.email || '',
        (value) => setFormData(prev => ({ ...prev, email: value }))
      )}

      {renderModalContactField(
        'phone',
        'Telefone',
        'tel',
        formData.telefone || formData.phone || '',
        (value) => {
          const digits = value.replace(/\D/g, '');
          const formatted = digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
          setFormData(prev => ({ ...prev, telefone: formatted, phone: formatted }));
        }
      )}

      {!preSelectedVehicle && (
        <div className={isLayout4 ? 'wl4-service-field' : 'space-y-2'}>
          {!isLayout4 && (
            <Label htmlFor="vehicle" className="text-sm font-medium">Veículo</Label>
          )}
          {isLayout4 && (
            <Label htmlFor="vehicle" className="wl4-service-label">Veículo</Label>
          )}
          <VehicleSelector
            vehicles={vehicles}
            selectedVehicle={selectedVehicles.vehicle}
            onSelect={handleFinancingVehicleSelect}
            placeholder="Selecionar veículo"
            serviceImage={serviceImage}
            serviceTitle={getServiceTitle()}
            layoutVariant={isLayout4 ? 'layout-4' : 'default'}
          />
        </div>
      )}

      {!isInlineFinancingOnDetail && renderModalMessageField(
        'message',
        formData.mensagem || formData.message || '',
        (value) => setFormData(prev => ({ ...prev, mensagem: value, message: value }))
      )}
    </div>
  ) : (
    <div className="space-y-4">
      {renderInline && service.service === '65dcb7898eca108d87c91dbc' && (
        isLayout4 ? (
          <h3 className="wl4-service-inline-title">
            {(() => {
              const IconComponent = getServiceIcon();
              return <IconComponent className="h-5 w-5 text-[var(--wl4-accent)]" />;
            })()}
            {serviceTitle}
          </h3>
        ) : (
          <CardTitle className="flex text-xl font-bold items-center gap-2">
            {serviceTitle}
          </CardTitle>
        )
      )}

      <div className="space-y-4">
        {sortedFields
          .filter(field =>
            field !== 'preco' &&
            field !== 'price' &&
            field !== 'segmento' &&
            field !== 'segment' &&
            field !== 'opcoes' &&
            field !== 'options' &&
            field !== 'opcao' &&
            field !== 'option' &&
            field !== 'servicos' &&
            field !== 'services' &&
            field !== 'servico' &&
            field !== 'service' &&
            field !== 'revisoes' &&
            field !== 'revisions' &&
            field !== 'revisao' &&
            field !== 'revision' &&
            field !== 'mensagem' &&
            field !== 'message' &&
            !(isInlineFinancingOnDetail && (field === 'veiculo' || field === 'vehicle'))
          )
          .map((field) => (
            <div key={field}>
              {field === 'veiculo' || field === 'vehicle' ? (
                <div className={isLayout4 ? 'wl4-service-field' : 'space-y-2'}>
                  {isLayout4 && (
                    <Label className="wl4-service-label">
                      {getFieldLabel(field)}{' '}
                      <span className="wl4-service-required">*</span>
                    </Label>
                  )}
                  {/* Use FIPE selector for specific services, regular selector for others */}
                  {(service.service === '65dcb7548eca108d87c91dbb' ||  // Avaliação
                    service.service === '65dcb7c58eca108d87c91dbd' ||  // Consignação
                    service.service === '6628fd535bda2ae51f6fc5ff' ||  // Peças e Acessórios
                    service.service === '65dcef098eca108d87c91dc7' ||  // Revisões
                    service.service === '65dceeac8eca108d87c91dc6') ? ( // Serviços
                    <FipeVehicleSelector
                      service={service}
                      value={formData[field] || ''}
                      onChange={(vehicleText, vehicleData) => {
                        setFormData(prev => ({
                          ...prev,
                          [field]: vehicleText,
                          'marca': vehicleData.brand,
                          'brand': vehicleData.brand,
                          'modelo': vehicleData.model,
                          'model': vehicleData.model,
                          'ano': vehicleData.year,
                          'year': vehicleData.year
                        }));
                      }}
                      serviceImage={serviceImage}
                      serviceTitle={getServiceTitle()}
                      layoutVariant={isLayout4 ? 'layout-4' : 'default'}
                    />
                  ) : (
                    <VehicleSelector
                      vehicles={vehicles}
                      selectedVehicle={selectedVehicles[field]}
                      onSelect={(vehicle) => handleVehicleSelect(field, vehicle)}
                      placeholder="VEÍCULO"
                      disabled={preSelectedVehicle}
                      serviceImage={serviceImage}
                      serviceTitle={getServiceTitle()}
                      layoutVariant={isLayout4 ? 'layout-4' : 'default'}
                    />
                  )}
                </div>
              ) : (
                renderModalTextField(field)
              )}
            </div>
          ))}
      </div>

      {service.options && service.options.length > 0 && service.service !== '65dcb7c58eca108d87c91dbd' && (
        <div className="space-y-2">
          <Label className={isLayout4 ? 'wl4-service-options-label' : 'text-sm font-medium'}>
            {service.service === '6628fd535bda2ae51f6fc5ff' || service.service === '65dcb8548eca108d87c91dbf'
              ? 'Segmento'
              : service.service === '65dceeac8eca108d87c91dc6'
                ? 'Serviços'
                : service.service === '65dcef098eca108d87c91dc7'
                  ? 'Revisões'
                  : 'Opções'}
          </Label>
          {service.multipleSelection ? (
            <div className={isLayout4 ? 'wl4-service-options' : 'space-y-2 mt-2 border rounded-lg p-3 max-h-[200px] overflow-y-auto'}>
              {(() => {
                const sortedOptions = service.service === '65dcb8548eca108d87c91dbf'
                  ? [...service.options].sort((a: any, b: any) => a.title.localeCompare(b.title))
                  : service.options;
                return sortedOptions;
              })().map((option: any, index: number) => (
                <div key={index} className={isLayout4 ? 'wl4-service-options-row' : 'flex items-center space-x-2'}>
                  <Checkbox
                    id={`option-${index}`}
                    checked={selectedOptions.includes(option.title)}
                    onCheckedChange={() => handleOptionToggle(option.title)}
                  />
                  <label
                    htmlFor={`option-${index}`}
                    className={isLayout4
                      ? undefined
                      : 'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'}
                  >
                    {option.title}
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <Select
              value={selectedOptions[0] || ''}
              onValueChange={handleSingleOptionChange}
            >
              <SelectTrigger className={isLayout4 ? 'wl4-service-select' : undefined}>
                <SelectValue placeholder={
                  service.service === '6628fd535bda2ae51f6fc5ff' || service.service === '65dcb8548eca108d87c91dbf'
                    ? 'Selecione um segmento'
                    : service.service === '65dceeac8eca108d87c91dc6'
                      ? 'Selecione um serviço'
                      : service.service === '65dcef098eca108d87c91dc7'
                        ? 'Selecione uma revisão'
                        : 'Selecione uma opção'
                } />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const sortedOptions = service.service === '65dcb8548eca108d87c91dbf'
                    ? [...service.options].sort((a: any, b: any) => a.title.localeCompare(b.title))
                    : service.options;
                  return sortedOptions;
                })().map((option: any, index: number) => (
                  <SelectItem key={index} value={option.title}>
                    {option.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {(requiredFields.includes('mensagem') || requiredFields.includes('message')) && !isInlineFinancingOnDetail && (
        renderModalMessageField(
          'mensagem',
          formData['mensagem'] || formData['message'] || '',
          (value) => {
            const field = requiredFields.includes('mensagem') ? 'mensagem' : 'message';
            handleInputChange(field, value);
          }
        )
      )}
    </div>
  );

  const footerContent = (
    <div className={renderInline
      ? (isLayout4 ? 'pt-6' : 'pt-6')
      : (isLayout4 ? 'wl4-service-modal-footer' : 'sticky bg-background border-t px-6 py-4 bottom-0 z-10')}>
      <Button
        onClick={handleSubmit}
        disabled={isSubmitting}
        className={isLayout4
          ? 'wl4-service-modal-submit'
          : 'w-full h-11 text-white transition-all duration-200'}
        style={isLayout4 ? undefined : {
          backgroundColor: buttonColor,
        }}
        onMouseEnter={(e) => {
          if (!isSubmitting && !isLayout4) {
            e.currentTarget.style.opacity = '0.9';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSubmitting && !isLayout4) {
            e.currentTarget.style.opacity = '1';
          }
        }}
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
            Enviando...
          </>
        ) : (
          <>Enviar</>
        )}
      </Button>
    </div>
  );

  // If renderInline, return form + footer
  if (renderInline) {
    return (
      <div className={isLayout4 ? 'layout-4 wl4-service-inline' : undefined} style={modalThemeStyle}>
        <div className={isLayout4 ? 'wl4-service-modal-body' : undefined}>
          {formContent}
          {renderServiceWarning()}
        </div>
        {footerContent}
      </div>
    );
  }

  const shouldRenderTrigger = !!children && !renderInline;

  // Otherwise, return the form in a Dialog
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {shouldRenderTrigger && (
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
      )}
      <DialogContent
        className={
          isLayout4
            ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
            : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
        }
        style={modalThemeStyle}
      >
        {serviceImage && (
          <DialogTitle className="sr-only">
            {getServiceTitle()}
          </DialogTitle>
        )}

        {renderModalHeader()}

        <div className={isLayout4 ? 'wl4-service-modal-body' : 'flex-1 overflow-y-auto px-6 py-6'}>
          {formContent}
          {renderServiceWarning()}
        </div>
        {footerContent}
      </DialogContent>
    </Dialog>
  );
};